import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, PowerOff, RefreshCw, Save, Upload, X } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/api/client";

/**
 * Admin Studio — Per-program certificate configuration editor (Phase 1e).
 *
 * Route: `/admin/academy/programs/:programId/certificate-config`.
 * RBAC:  edit_academy_content + `academy_admin_studio_enabled`.
 *
 * All updates PATCH the saved config directly. Preview button downloads a
 * watermarked SAMPLE PDF using the CURRENT SAVED config (P5) — no
 * in-flight overrides.
 */
export default function AdminCertificateConfig() {
  const { programId } = useParams();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      const res = await api.get(`/admin/academy/programs/${programId}/certificate-config`);
      setConfig(res.data);
      setStatus("ready");
    } catch (err) {
      if (err?.response?.status === 404) {
        // Auto-create defaults
        try {
          const created = await api.post(`/admin/academy/programs/${programId}/certificate-config`);
          setConfig(created.data);
          setStatus("ready");
        } catch (createErr) {
          setError(formatApiError(createErr));
          setStatus("error");
        }
        return;
      }
      setError(formatApiError(err));
      setStatus("error");
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (partial, opts = {}) => {
    setSaving(true); setError(null);
    try {
      const res = await api.patch(`/admin/academy/programs/${programId}/certificate-config`, partial);
      setConfig(res.data);
      if (opts.notice) setNotice(opts.notice);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const activate = async () => {
    setSaving(true); setError(null);
    try {
      const res = await api.post(`/admin/academy/programs/${programId}/certificate-config/activate`);
      setConfig(res.data);
      setNotice("Certificate configuration activated. Learners can now claim certificates.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    setSaving(true); setError(null);
    try {
      const res = await api.post(`/admin/academy/programs/${programId}/certificate-config/deactivate`);
      setConfig(res.data);
      setNotice("Certificate configuration deactivated. Existing certificates remain valid.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (kind, file) => {
    const fd = new FormData();
    fd.append("upload", file);
    setSaving(true); setError(null);
    try {
      await api.post(
        `/admin/academy/programs/${programId}/certificate-config/${kind}-asset`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      await load();
      setNotice(`${kind} asset uploaded.`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const clearAsset = async (kind) => {
    setSaving(true); setError(null);
    try {
      await api.delete(`/admin/academy/programs/${programId}/certificate-config/${kind}-asset`);
      await load();
      setNotice(`${kind} asset cleared.`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (status === "loading") return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 text-v2-text-muted"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>
  );
  if (status === "error") return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 text-red-400">{error}</div>
  );

  return (
    <div data-testid="admin-certificate-config" className="max-w-4xl mx-auto px-4 md:px-8 py-10">
      <Link to="/admin/academy/programs" className="inline-flex items-center gap-1 text-xs text-v2-text-muted hover:text-v2-text mb-4">
        <ArrowLeft className="w-3 h-3" /> Programs
      </Link>
      <header className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">Academy Studio</p>
        <h1 className="mt-1 text-[32px] font-semibold text-v2-text" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
          Certificate Configuration
        </h1>
        <div className="mt-2 flex items-center gap-3">
          <PublicationPill state={config?.publication_state} />
          <span className="text-xs text-v2-text-muted">Template version {config?.template_version}</span>
        </div>
      </header>

      {notice && (
        <div className="mb-4 rounded-md border border-emerald-400/40 bg-emerald-400/5 text-emerald-300 p-3 text-sm flex items-start gap-2" data-testid="config-notice">
          <CheckCircle2 className="w-4 h-4 mt-0.5" />
          <div className="flex-1">{notice}</div>
          <button onClick={() => setNotice(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/5 text-red-300 p-3 text-sm" data-testid="config-error">{error}</div>
      )}

      <section className="border border-v2-border rounded-lg p-5 bg-v2-surface mb-6">
        <h2 className="text-sm font-medium text-v2-text mb-4">Certificate copy</h2>
        <TextField label="Display title" testid="cfg-display-title" value={config.display_title || ""}
          onSave={(v) => patch({ display_title: v })} disabled={saving} />
        <TextArea label="Completion statement" testid="cfg-completion-statement" value={config.completion_statement || ""}
          onSave={(v) => patch({ completion_statement: v })} disabled={saving} />
        <TextField label="Issuer name" testid="cfg-issuer-name" value={config.issuer_name || ""}
          onSave={(v) => patch({ issuer_name: v })} disabled={saving} />
        <TextField label="Signatory name" testid="cfg-signatory-name" value={config.signatory_name || ""}
          onSave={(v) => patch({ signatory_name: v })} disabled={saving} required />
        <TextField label="Signatory title" testid="cfg-signatory-title" value={config.signatory_title || ""}
          onSave={(v) => patch({ signatory_title: v })} disabled={saving} required />
      </section>

      <section className="border border-v2-border rounded-lg p-5 bg-v2-surface mb-6">
        <h2 className="text-sm font-medium text-v2-text mb-4">Signature & seal</h2>
        <AssetRow label="Signature image" kind="signature" gridfsRef={config.signature_asset_ref}
          onUpload={(f) => uploadAsset("signature", f)} onClear={() => clearAsset("signature")} disabled={saving} />
        <AssetRow label="Seal image" kind="seal" gridfsRef={config.seal_asset_ref}
          onUpload={(f) => uploadAsset("seal", f)} onClear={() => clearAsset("seal")} disabled={saving} />
        <p className="text-xs text-v2-text-muted mt-2">PNG or JPEG · Max 512 KB · Max 2000×2000 · EXIF stripped on upload.</p>
      </section>

      <section className="border border-v2-border rounded-lg p-5 bg-v2-surface mb-6">
        <h2 className="text-sm font-medium text-v2-text mb-4">Validity & verification</h2>
        <ToggleRow label="Enable expiration" testid="cfg-expiration-enabled"
          checked={!!config.expiration_enabled}
          onChange={(v) => patch({
            expiration_enabled: v,
            validity_period_days: v ? (config.validity_period_days || 365) : null,
          })} disabled={saving} />
        {config.expiration_enabled && (
          <NumberField label="Validity period (days)" testid="cfg-validity-days" min={1} max={36500}
            value={config.validity_period_days || 365}
            onSave={(v) => patch({ validity_period_days: v })} disabled={saving} />
        )}
        <ToggleRow label="Public verification enabled" testid="cfg-public-verification"
          checked={!!config.public_verification_enabled}
          onChange={(v) => patch({ public_verification_enabled: v })} disabled={saving} />
        <ToggleRow label="Revocation capability enabled" testid="cfg-revocation-capability"
          checked={!!config.revocation_capability_enabled}
          onChange={(v) => patch({ revocation_capability_enabled: v })} disabled={saving} />
      </section>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <div className="flex gap-2">
          <PreviewButton programId={programId} />
          <button data-testid="cfg-reload" onClick={load} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-v2-border text-v2-text text-xs hover:bg-white/5">
            <RefreshCw className="w-3.5 h-3.5" /> Reload
          </button>
        </div>
        <div className="flex gap-2">
          {config.publication_state !== "active" ? (
            <button data-testid="cfg-activate" onClick={activate}
              disabled={saving}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#A78BFA] text-black text-sm font-medium disabled:opacity-60 hover:bg-[#9D7AEA]">
              <CheckCircle2 className="w-4 h-4" /> Activate
            </button>
          ) : (
            <button data-testid="cfg-deactivate" onClick={deactivate}
              disabled={saving}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-red-500/40 text-red-300 text-sm hover:bg-red-500/10">
              <PowerOff className="w-4 h-4" /> Deactivate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewButton({ programId }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      const res = await api.post(
        `/admin/academy/programs/${programId}/certificate-config/preview`,
        {},
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally { setBusy(false); }
  };
  return (
    <button data-testid="cfg-preview" onClick={click} disabled={busy}
      className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-v2-border text-v2-text text-xs hover:bg-white/5">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 rotate-180" />}
      Preview PDF
    </button>
  );
}

function PublicationPill({ state }) {
  const map = {
    active: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5",
    draft: "text-amber-400 border-amber-400/40 bg-amber-400/5",
    inactive: "text-v2-text-muted border-v2-border",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${map[state] || map.draft}`}>{state || "draft"}</span>;
}

function TextField({ label, value, onSave, testid, disabled, required }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <label className="block mb-4">
      <span className="text-xs text-v2-text-muted">{label}{required && <span className="text-red-400 ml-1">*</span>}</span>
      <div className="flex gap-2 mt-1">
        <input data-testid={testid} disabled={disabled} value={v} onChange={(e) => setV(e.target.value)}
          className="flex-1 bg-v2-bg border border-v2-border rounded-md px-3 py-2 text-sm text-v2-text" />
        <button
          data-testid={`${testid}-save`}
          disabled={disabled || v === value}
          onClick={() => onSave(v)}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-v2-border text-v2-text text-xs disabled:opacity-40"
        >
          <Save className="w-3 h-3" /> Save
        </button>
      </div>
    </label>
  );
}

function TextArea({ label, value, onSave, testid, disabled }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <label className="block mb-4">
      <span className="text-xs text-v2-text-muted">{label}</span>
      <textarea data-testid={testid} disabled={disabled} value={v} onChange={(e) => setV(e.target.value)}
        rows={3}
        className="mt-1 w-full bg-v2-bg border border-v2-border rounded-md px-3 py-2 text-sm text-v2-text" />
      <div className="flex justify-end mt-1">
        <button
          data-testid={`${testid}-save`}
          disabled={disabled || v === value}
          onClick={() => onSave(v)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-v2-border text-v2-text text-xs disabled:opacity-40"
        >
          <Save className="w-3 h-3" /> Save
        </button>
      </div>
    </label>
  );
}

function NumberField({ label, value, onSave, testid, disabled, min, max }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <label className="block mb-4">
      <span className="text-xs text-v2-text-muted">{label}</span>
      <div className="flex gap-2 mt-1">
        <input type="number" data-testid={testid} disabled={disabled} min={min} max={max}
          value={v} onChange={(e) => setV(Number(e.target.value))}
          className="flex-1 bg-v2-bg border border-v2-border rounded-md px-3 py-2 text-sm text-v2-text" />
        <button
          data-testid={`${testid}-save`}
          disabled={disabled || v === value}
          onClick={() => onSave(Number(v))}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-v2-border text-v2-text text-xs disabled:opacity-40"
        >
          <Save className="w-3 h-3" /> Save
        </button>
      </div>
    </label>
  );
}

function ToggleRow({ label, checked, onChange, testid, disabled }) {
  return (
    <label className="flex items-center justify-between gap-4 mb-3">
      <span className="text-sm text-v2-text">{label}</span>
      <input type="checkbox" data-testid={testid} disabled={disabled}
        checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-[#A78BFA]" />
    </label>
  );
}

function AssetRow({ label, kind, gridfsRef, onUpload, onClear, disabled }) {
  const inputId = `asset-${kind}-file`;
  return (
    <div className="flex items-center justify-between gap-3 mb-3 border border-v2-border rounded-md p-3 bg-v2-bg">
      <div className="text-sm text-v2-text">
        {label} {gridfsRef ? <span className="text-xs text-emerald-400 ml-2">uploaded</span> : <span className="text-xs text-v2-text-muted ml-2">none</span>}
      </div>
      <div className="flex gap-2">
        <input id={inputId} type="file" accept="image/png,image/jpeg"
          data-testid={`upload-${kind}-asset`}
          disabled={disabled}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
        <label htmlFor={inputId} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-v2-border text-v2-text text-xs cursor-pointer hover:bg-white/5">
          <Upload className="w-3 h-3" /> Upload
        </label>
        {gridfsRef && (
          <button data-testid={`clear-${kind}-asset`} disabled={disabled} onClick={onClear}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-v2-border text-v2-text text-xs hover:bg-white/5">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}
